import * as signalR from "@microsoft/signalr";
import { getLatestTaskList } from "../../redux/actions/Firebase/getLatestTaskListAction";
import { getLatestGtEventsAction } from "../../redux/actions/Calendar/getLatestGtEventsAction";
import { getLatestPrimaryEventsAction } from "../../redux/actions/Calendar/getLatestPrimaryEventsAction";
import { syncEventAdded, syncEventUpdated, syncEventDeleted } from "../../redux/actions/Calendar/syncCalendarEventsAction";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { getItem, clear } from "../../utils/storageHelper";
import { isDragInProgress } from "../dragFunctions/dragOperationState";
import { auth } from "../../firebase/firebaseConfig";


let connection, calendarConnection = null;
let appStateListener = null;
let appStateListenerOperation = Promise.resolve();
let visibilityListenerAdded = false;

// Custom retry policy with more aggressive reconnection for mobile
// Keeps trying indefinitely with exponential backoff instead of giving up after 4 attempts
const customRetryPolicy = {
    nextRetryDelayInMilliseconds: (retryContext) => {
        // Retry delays: 0, 1s, 2s, 5s, 10s, 30s, then 30s forever
        const delays = [0, 1000, 2000, 5000, 10000, 30000];
        const index = Math.min(retryContext.previousRetryCount, delays.length - 1);
        return delays[index];
    }
};

// Handle app state changes (background/foreground) on native platforms
const enqueueAppStateListenerOperation = (operation) => {
    const result = appStateListenerOperation.then(operation);
    appStateListenerOperation = result.catch(() => {});
    return result;
};

const setupAppStateListener = (dispatch) => {
    if (!Capacitor.isNativePlatform()) {
        return Promise.resolve();
    }

    return enqueueAppStateListenerOperation(async () => {
        const previousListener = appStateListener;
        appStateListener = null;
        await previousListener?.remove();
        appStateListener = await App.addListener('appStateChange', async ({ isActive }) => {
            if (isActive) {
                // App came to foreground - check connection and refresh data
                await handleAppResume(dispatch);
            }
        });
    });
};

// Handle visibility changes on web browsers
const setupVisibilityChangeListener = (dispatch) => {
    if (!Capacitor.isNativePlatform() && !visibilityListenerAdded) {
        document.addEventListener('visibilitychange', async () => {
            if (document.visibilityState === 'visible') {
                await handleAppResume(dispatch);
            }
        });
        visibilityListenerAdded = true;
    }
};

// Common handler for when app resumes (either from background on native or tab becomes visible on web)
const handleAppResume = async (dispatch) => {
    if (connection) {
        const state = connection.state;
        
        if (state === signalR.HubConnectionState.Disconnected) {
            try {
                await connection.start();
                dispatch(getLatestTaskList());
            } catch (err) {
                console.error("Failed to reconnect SignalR:", err);
            }
        } else if (state === signalR.HubConnectionState.Connected) {
            // Fetch latest data to ensure we're in sync after resume
            dispatch(getLatestTaskList());
        }
    }
};

export const connectToSignalR = async (dispatch) => {
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
        await setupAppStateListener(dispatch);
        return;
    }

    const hubUrl = `${import.meta.env.VITE_PUBLIC_DOMAIN}FileSyncHub`;
    
    // Configure authentication options
    const options = { 
        logger: signalR.LogLevel.Warning,
        // CRITICAL FIX: Add Firebase token to all SignalR requests for authentication
        // This is required for BOTH web and native platforms to properly authenticate
        accessTokenFactory: async () => {
            try {
                const user = auth.currentUser;
                if (!user) {
                    console.warn('[SignalR] No Firebase user available for token');
                    return null;
                }
                const token = await user.getIdToken();
                console.log('[SignalR] Token retrieved for FileSyncHub connection');
                return token;
            } catch (error) {
                console.error('[SignalR] Error getting Firebase token:', error);
                return null;
            }
        }
    };
    
    // For native platforms, FORCE ServerSentEvents or LongPolling (NOT WebSockets)
    // WebSocket connections don't work properly in Capacitor WebView
    if (Capacitor.isNativePlatform()) {
        // Only allow ServerSentEvents and LongPolling - these work better in native WebView
        options.transport = signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling;
        options.skipNegotiation = false;
    } else {
        // Web: use WebSockets with withCredentials for CORS
        options.withCredentials = true;
    }

    connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, options)
        .withAutomaticReconnect(customRetryPolicy)
        .configureLogging(signalR.LogLevel.Warning)
        .build();

    connection.keepAliveIntervalInMilliseconds = 30000; 
    connection.serverTimeoutInMilliseconds = 90000;

    // Register event handlers
    connection.on('FileUpdated', () => {
       
        dispatch(getLatestTaskList());
    });
    
    connection.on('ClearStorage', () => {
        clear();
        window.sessionStorage.clear();
    });

    // Handle successful reconnection - fetch latest data since we may have missed updates
    connection.onreconnected(() => {
        dispatch(getLatestTaskList());
    });

    connection.onreconnecting((error) => {
        console.warn("SignalR reconnecting...", error?.message);
    });

    connection.onclose((error) => {
        // `error` is undefined on intentional `stop()` (e.g. StrictMode / HMR unmount).
        if (error) {
            console.warn("SignalR connection closed:", error.message || error);
        }
    });

    try {
        await connection.start();
        
        // Setup app state change listener for native platforms (iOS/Android)
        await setupAppStateListener(dispatch);
        
        // Setup visibility change listener for web browsers
        setupVisibilityChangeListener(dispatch);
        
    } catch (err) {
        console.error("Error establishing SignalR connection:", err);
    }
};

export const connectToCalendarSyncHub = async (dispatch) => {
    if (calendarConnection && calendarConnection.state === signalR.HubConnectionState.Connected) {
        return;
    }
    
    const hubUrl = `${import.meta.env.VITE_PUBLIC_DOMAIN}CalendarSyncHub`;
    const options = {
        // CRITICAL FIX: Add Firebase token to all SignalR requests for authentication
        // This is required for BOTH web and native platforms to properly authenticate
        accessTokenFactory: async () => {
            try {
                const user = auth.currentUser;
                if (!user) {
                    console.warn('[SignalR] No Firebase user available for token');
                    return null;
                }
                const token = await user.getIdToken();
                console.log('[SignalR] Token retrieved for CalendarSyncHub connection');
                return token;
            } catch (error) {
                console.error('[SignalR] Error getting Firebase token:', error);
                return null;
            }
        }
    };
    
    // For native platforms, FORCE ServerSentEvents or LongPolling (NOT WebSockets)
    if (Capacitor.isNativePlatform()) {
        options.transport = signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling;
        options.skipNegotiation = false;
    } else {
        // Web: use WebSockets with withCredentials for CORS
        options.withCredentials = true;
    }
    
    calendarConnection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, options)
        .withAutomaticReconnect()
        .build();
    calendarConnection.keepAliveIntervalInMilliseconds = 300000; 
    calendarConnection.serverTimeoutInMilliseconds = 600000;
    calendarConnection.on("CalendarUpdated", async function (eventData) {
        try {
            // Get fresh access token, calendar IDs and provider (works for both web and native)
            const accessToken = await getItem("calendar_access_token");
            const calendarIdsString = await getItem("calendar");
            const calendarProvider = await getItem("calendarProvider");
            
            if (!accessToken) {
                console.error("Access token not found in storage");
                return;
            }
            
            if (!calendarIdsString || !calendarProvider) {
                console.error("Calendar IDs or provider not found in storage");
                return;
            }

            const calendarIds = JSON.parse(calendarIdsString);

            const params = {
                accessToken: accessToken,
                type: calendarProvider,
                calendarId: eventData === 'primary' ? calendarIds.Primary : calendarIds.GlobalTasks
            };

            if (eventData === 'primary') {
                dispatch(getLatestPrimaryEventsAction(params));
            } else {
                dispatch(getLatestGtEventsAction(params));
            }

        } catch (error) {
            console.error("Error handling CalendarUpdated event:", error);
        }
    });
    calendarConnection.on("EventAdded", function (eventData) {
        dispatch(syncEventAdded(eventData));
    });

    calendarConnection.on("EventUpdated", function (eventData) {
        dispatch(syncEventUpdated(eventData));
    });

    calendarConnection.on("EventDeleted", function (eventData) {
        const eventJson = JSON.parse(eventData);
        dispatch(syncEventDeleted(eventJson));
    });
    try {
        await calendarConnection.start();
    } catch (err) {
        console.error("Error establishing CalendarSyncHub connection:", err);
    }
};
export const isCalendarConnected = () => {
    return calendarConnection && calendarConnection.state === signalR.HubConnectionState.Connected;
};
export const stopSignalRConnection = async () => {
    // Remove app state listener for native platforms
    try {
        await enqueueAppStateListenerOperation(async () => {
            const listener = appStateListener;
            appStateListener = null;
            await listener?.remove();
        });
    } catch (err) {
        console.error("Error removing app state listener:", err);
    }
    
    if (connection && connection.state !== signalR.HubConnectionState.Disconnected) {
        try {
            await connection.stop();
            connection = null;
        } catch (err) {
            console.error("Error stopping SignalR connection:", err);
        }
    }
};